import React, { FC, ReactNode } from 'react';
import {
  DesktopDatePicker as DatePicker,
  DesktopDatePickerProps,
} from '@mui/x-date-pickers/DesktopDatePicker';
import { Typography, Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import StyledInput from '../inputs/StyledInput';
import { Control, Controller, FieldValues } from 'react-hook-form';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { RedAsterisk } from '../RedAsterisk';

dayjs.extend(utc);

const minimumAcceptableDate = dayjs.utc(new Date(1900, 0, 1));
const invalidDate = dayjs.utc(new Date('invalid'));

type Props<T extends FieldValues = any> = {
  name: string;
  control: Control<T>;
  isDisabled?: boolean;
  touched?: boolean;
  placeholder?: string;
  fullWidth?: boolean;
  label?: ReactNode;
  minDate?: Date;
  maxDate?: Date;
  readOnly?: boolean;
  required?: boolean;
  views?: DesktopDatePickerProps<Date>['views'];
} & Omit<DesktopDatePickerProps<Date>, 'onChange' | 'value' | 'renderInput'>;

const DatePickerField: FC<Props> = (props) => {
  const {
    name,
    control,
    label,
    isDisabled,
    minDate,
    maxDate,
    readOnly,
    touched: defaultTouched,
    views,
    placeholder,
    fullWidth,
    required = false,
  } = props;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const { isTouched: fieldTouched, error } = fieldState;
        const touched = defaultTouched || fieldTouched;

        const { value, onChange } = field;

        function _renderHelperText() {
          if (touched && error) {
            return error?.message;
          }
        }

        function _onChange(date) {
          const isValidDate = dayjs.utc(date).isValid();
          const nextDate =
            (isValidDate && date) > minimumAcceptableDate ? date : invalidDate;
          if (date) {
            try {
              // YYYY-MM-dd
              onChange(dayjs.utc(nextDate).format('YYYY-MM-DD'));
            } catch (error) {
              console.error(error);
              onChange(nextDate);
            }
          } else {
            onChange(date);
          }
        }
        return (
          <Box>
            <Typography variant="body1" component="span">
              {label}
              {required && <RedAsterisk />}
            </Typography>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              dateLibInstance={dayjs.utc}
            >
              <DatePicker
                disabled={isDisabled}
                readOnly={readOnly}
                views={views}
                slots={{
                  textField: StyledInput as any,
                }}
                slotProps={{
                  textField: {
                    error: Boolean(touched && error),
                    onBlur: field.onBlur,
                    id: field.name,
                    helperText: _renderHelperText(),
                    placeholder,
                    style: { marginTop: 10.25 },
                  },
                  // here we have to pass the fullWidth prop because for some reason
                  // the fullWidth prop is not taking effect on the TextField component
                  field: {
                    fullWidth,
                  } as any,
                }}
                value={dayjs.utc(value)}
                onChange={_onChange}
                maxDate={dayjs.utc(maxDate)}
                minDate={dayjs.utc(minDate)}
              />
            </LocalizationProvider>
          </Box>
        );
      }}
    />
  );
};

export default DatePickerField;
